import { useCallback, useState } from 'react';
import { createSprint, updateSprint } from '../../api/endpoints';
import { toApiError } from '../../api/errors';
import type { Id, Sprint } from '../../api/types';
import type { SprintFormMode } from './SprintFormDialog';
import { createSprintBody, isEmptyEdit, sprintEditBody, type SprintDraft } from './sprint-form';

/**
 * CREATING AND EDITING A SPRINT — PH-23's write half.
 *
 * ⭐ ONE HOOK FOR BOTH, because they are one journey with one form: the same fields, the same
 * refusals (`a sprint named '…' already exists in this project` comes back from either), and the
 * same "the dialog stays open and keeps what you typed" behaviour. Splitting them would duplicate
 * every one of those and give two places for the error handling to drift.
 *
 * ⛔ THE ERROR IS THE API'S OWN WORDS — `reason` first, exactly as `useCompleteSprint` and
 * `useRankReorder` do it. For these endpoints that is:
 *   · `a sprint named 'APFS v1.0.35' already exists in this project`   (409, the common one)
 *   · `the sprint end date is before its start date`                   (400)
 *   · `that version belongs to a different project` / `version not found` (400)
 *   · `only an active member of this project may modify its sprints`   (403)
 *   · `the request body or query was not valid`                        (400, zod — see `draftProblem`)
 * Every one of those says something "Could not save the sprint" does not, and the first tells the
 * reader the one thing they need in order to succeed on the second attempt.
 *
 * ⭐ THE DIALOG STAYS OPEN ON FAILURE, with every field as typed. Closing it and dropping a banner
 * elsewhere loses the form and the message at the same time.
 */
export interface SprintEditorState {
  /** The form that is open, or `null`. */
  open: SprintFormMode | null;
  busy: boolean;
  /** The API's own explanation of a refusal — something WAS sent. */
  error: string | null;
  /**
   * OUR refusal — nothing was sent. PH-60.
   *
   * 🔴 THIS FIELD DID NOT EXIST, AND THE CONSEQUENCE WAS A FALSE SENTENCE ON SCREEN. The
   * "nothing has changed" refusal was written to `error`, which `SprintFormDialog` renders under
   * the heading "The API refused this sprint" — for a request the API never received.
   * ⭐ The dialog's own comment already forbade this ("TWO SOURCES OF 'NO', KEPT APART"); the hook
   * bypassed it. Same defect, same shape, as PH-57's ticket form — which is why the fix is copied
   * from `features/tickets/useTicketEditor.ts` rather than reinvented.
   */
  problem: string | null;
  /** What happened, once it has succeeded. */
  outcome: string | null;
  create: () => void;
  edit: (sprint: Sprint) => void;
  cancel: () => void;
  dismissOutcome: () => void;
  submit: (draft: SprintDraft) => void;
}

export function useSprintEditor(
  projectId: Id | null,
  onSaved: (sprint: Sprint) => void,
): SprintEditorState {
  const [open, setOpen] = useState<SprintFormMode | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const create = useCallback(() => {
    // ⚠️ Belt and braces, and NOT claimed as a tested path: the button that calls this is rendered
    //    only inside `sprintsResource.data`, which cannot load without a project. It is here so the
    //    hook has one answer for a null project rather than depending on its caller for correctness.
    if (!projectId) return;
    setOpen({ kind: 'create' });
    setError(null);
    setProblem(null);
    setOutcome(null);
  }, [projectId]);

  const edit = useCallback((sprint: Sprint) => {
    setOpen({ kind: 'edit', sprint });
    setError(null);
    setProblem(null);
    setOutcome(null);
  }, []);

  const cancel = useCallback(() => {
    /**
     * ⛔ Not while a request is in flight — the same reasoning as `useCompleteSprint.cancel`: if the
     *   dialog is dismissed mid-write a FAILURE has nowhere to appear and the refusal is silent.
     * ⚠️ MEASURED, NOT ASSUMED, and the honest half is worth writing down: breaking this guard alone
     *   does NOT turn the suite red, because the Cancel button carries `disabled={busy}` and the
     *   path is unreachable through the UI. The `disabled` attribute is the line under test; this is
     *   the second line behind it.
     */
    setBusy((inFlight) => {
      if (!inFlight) {
        setOpen(null);
        setError(null);
        setProblem(null);
      }
      return inFlight;
    });
  }, []);

  const submit = useCallback(
    (draft: SprintDraft) => {
      const mode = open;
      if (!mode || busy) return;

      setBusy(true);
      setError(null);
      setProblem(null);

      void (async () => {
        try {
          if (mode.kind === 'create') {
            if (!projectId) return;
            const sprint = await createSprint(projectId, createSprintBody(draft));
            setOpen(null);
            // ⭐ The SERVER's sprint, and its state said out loud. `planned` is the only state the
            //   API will create, and the reader's next action depends on knowing that.
            setOutcome(
              `Created ${sprint.name}. It is Planned — press Start sprint on it when the team begins.`,
            );
            onSaved(sprint);
            return;
          }

          const edited = sprintEditBody(mode.sprint, draft);
          /**
           * ⛔ NOTHING CHANGED IS NOT A SAVE, and it is refused HERE rather than sent. The API would
           *   accept the empty PATCH quite happily — `updateSprint` skips the UPDATE when it has no
           *   assignments and returns the row — so this is not about avoiding a refusal. It is about
           *   not reporting "Saved" for a request that wrote nothing, which is the same
           *   "looks like it worked" failure as a silent write.
           */
          if (isEmptyEdit(edited)) {
            // ⭐ `problem`, NOT `error` — nothing was sent, so the API must not be blamed for it.
            setProblem('Nothing has changed, so nothing was saved.');
            return;
          }

          const sprint = await updateSprint(mode.sprint.id, edited);
          setOpen(null);
          setOutcome(`Saved ${sprint.name}.`);
          onSaved(sprint);
        } catch (caught) {
          const path =
            mode.kind === 'create' ? `/projects/${projectId}/sprints` : `/sprints/${mode.sprint.id}`;
          const apiError = toApiError(caught, path);
          setError(apiError.reason ?? apiError.message);
        } finally {
          setBusy(false);
        }
      })();
    },
    [open, busy, projectId, onSaved],
  );

  return {
    open,
    busy,
    error,
    problem,
    outcome,
    create,
    edit,
    cancel,
    dismissOutcome: () => setOutcome(null),
    submit,
  };
}
